import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

export enum FeatureFlag {
  DisablePriceEditing = 'disablePriceEditing',
  DiscontinueItem = 'itemManagementServiceDiscontinueItem',
  RequireInventoryItemDimensionFields = 'imsRequireInventoryItemDimensionFields',
  BrandedItems = 'imsBrandedItems',
  CustomLocationItems = 'item-management-service-40856-add-location-items',
  RichTextEditorForItemDescription = 'richTextEditorForItemDescription',
  EditableItemTags = 'imsEditableItemTags',
  canUpdatePerishability = 'canUpdatePerishability',
  itemManagementServiceBrandAssociationEnabled = 'itemManagementServiceBrandAssociationEnabled',
  itemManagementWebPricingRestructure = 'itemManagementWebPricingRestructure',
  DisplayProcurementSection = 'displayProcurementSection',
  shippingDimensionFields = 'imsShippingDimensionFields',
  itemManagementServiceSelfServiceFulfillmentWarehouse = 'itemManagementServiceSelfServiceFulfillmentWarehouse',
  invItemMarketPriceFlag = 'invItemMarketPriceFlag',
  invItemMarketPriceVendorValidation = 'invItemMarketPriceVendorValidation',
  ItemManagementWebBulkDiscontinue = 'itemManagementWebBulkDiscontinue',
  CategoryImageFallback = 'itemManagementServiceCategoryImageFallback',
  selfServiceMarketingCollections = 'itemManagementSelfServiceMarketingCollections',
  selfServiceMasterHomepages = 'itemManagementSelfServiceMasterHomepages',
  ItemIssues = 'itemManagementServiceItemIssues',
  PoundsUomProcurement = 'imsMassUomProcurement',
  DeleteCollection = 'itemManagementServiceDeleteCollection',
  CatalogOosBuffer = 'item-management-service-catalog-oos-buffer',
  ItemInactivation = 'itemManagementServiceItemInactivation',
  // LD flag key is `item-management-service-new-item-pinning`; the LD React SDK camelCases keys in
  // useFlags(), so the enum value must be the camelCased form for the lookup to resolve.
  NewItemPinning = 'itemManagementServiceNewItemPinning',
  // Cross-market New Items pinning. Gated separately from NewItemPinning so it can roll
  // out on its own without touching existing single-market pinning. LD flag key is
  // `item-management-service-cross-market-new-item-pinning`; camelCased for the useFlags() reason above.
  CrossMarketNewItemPinning = 'itemManagementServiceCrossMarketNewItemPinning',
  // LD flag key is `item-management-service-substitution-overrides`; camelCased form for the same
  // useFlags() reason as NewItemPinning above.
  SubstitutionOverrides = 'itemManagementServiceSubstitutionOverrides',
  // Per-market banners on catalog category pages. LD key is `sc-65857-category-banners`;
  // camelCased for the same useFlags() reason as NewItemPinning above.
  CategoryBanners = 'sc65857CategoryBanners',
  // Custom destination links for category banners. LD key is
  // `sc-65857-category-banner-custom-destinations`; camelCased for the same
  // useFlags() reason as NewItemPinning above.
  CategoryBannerCustomDestinations = 'sc65857CategoryBannerCustomDestinations',
  // Marking a product not-yet-orderable in a market. The LD key is already camelCase, so unlike
  // the hyphenated keys above it is used verbatim and useFlags() camelCasing is a no-op.
  MarketOrderability = 'itemManagementServiceMarketOrderability',
  // Writing wording of your own for a coming soon market, rather than taking the standard
  // wording. Released separately from MarketOrderability above because the state is what the
  // onboarding needs and a per-market message is an editorial decision that can wait. LD key is
  // camelCase to match its sibling.
  MarketOrderabilityCustomMessage = 'itemManagementServiceMarketOrderabilityCustomMessage',
  // Standing orders and the out-of-stock check set per market. Turn on only after IMS's
  // itemManagementServiceMarketItemFlagMirror is off, or item saves revert the market values.
  MarketItemFlags = 'itemManagementServiceMarketItemFlags',
  // The lifetime limit per location on items and samples. Hidden until so-api enforces the limit, so
  // nobody sets one expecting it to work. LD key is `sc-67120-lifetime-limit-per-location`;
  // camelCased for the same useFlags() reason as NewItemPinning above.
  LifetimeLimitPerLocation = 'sc67120LifetimeLimitPerLocation',
}

type FlagDefaultType = {
  [key in FeatureFlag]: boolean;
};

const FLAG_DEFAULTS: FlagDefaultType = {
  sc65857CategoryBanners: false,
  sc65857CategoryBannerCustomDestinations: false,
  disablePriceEditing: true,
  itemManagementServiceDiscontinueItem: true,
  imsRequireInventoryItemDimensionFields: false,
  imsBrandedItems: true,
  itemManagementServiceBrandAssociationEnabled: true,
  'item-management-service-40856-add-location-items': true,
  richTextEditorForItemDescription: false,
  imsEditableItemTags: false,
  canUpdatePerishability: false,
  itemManagementWebPricingRestructure: false,
  displayProcurementSection: false,
  imsShippingDimensionFields: false,
  itemManagementServiceSelfServiceFulfillmentWarehouse: false,
  invItemMarketPriceFlag: false,
  invItemMarketPriceVendorValidation: false,
  itemManagementWebBulkDiscontinue: false,
  itemManagementServiceCategoryImageFallback: false,
  itemManagementSelfServiceMarketingCollections: false,
  itemManagementSelfServiceMasterHomepages: false,
  imsMassUomProcurement: false,
  itemManagementServiceItemIssues: false,
  itemManagementServiceDeleteCollection: false,
  'item-management-service-catalog-oos-buffer': false,
  itemManagementServiceItemInactivation: false,
  itemManagementServiceNewItemPinning: false,
  itemManagementServiceCrossMarketNewItemPinning: false,
  itemManagementServiceSubstitutionOverrides: false,
  itemManagementServiceMarketOrderability: false,
  itemManagementServiceMarketOrderabilityCustomMessage: false,
  itemManagementServiceMarketItemFlags: false,
  sc67120LifetimeLimitPerLocation: false,
};

export const useFeatureFlipper = (flag: FeatureFlag): boolean | undefined => {
  const allFlags = useFlags();
  // Read the flag value DURING RENDER (not only inside the effect below) so
  // LaunchDarkly v3's flag-read tracking subscribes this component to live
  // updates for it. A read that happens only in an effect never registers with
  // the tracking proxy, so the component would not re-render on flag changes
  // until a page reload.
  const flagValue: boolean | undefined = allFlags[flag];
  const [isEnabled, setIsEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    setIsEnabled(flagValue ?? FLAG_DEFAULTS[flag]);
  }, [flag, flagValue]);

  return isEnabled;
};

export const useIsPriceEditingDisabled = (): boolean => {
  return useFeatureFlipper(FeatureFlag.DisablePriceEditing) ?? true;
};

export const useShippingDimensionFields = (): boolean => {
  return useFeatureFlipper(FeatureFlag.shippingDimensionFields) ?? false;
};

export const useIsPricingRestructureEnabled = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.itemManagementWebPricingRestructure) ?? false
  );
};

export const useIsItemBrandAssociationEnabled = (): boolean => {
  return (
    useFeatureFlipper(
      FeatureFlag.itemManagementServiceBrandAssociationEnabled,
    ) ?? false
  );
};

export const useCanUpdatePerishability = (): boolean => {
  return useFeatureFlipper(FeatureFlag.canUpdatePerishability) ?? false;
};

export const useCanDiscontinueItem = (): boolean => {
  return useFeatureFlipper(FeatureFlag.DiscontinueItem) ?? true;
};

export const useHasBrandedItems = (): boolean => {
  return useFeatureFlipper(FeatureFlag.BrandedItems) ?? true;
};

export const useRichTextEditorForItemDescription = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.RichTextEditorForItemDescription) ?? true
  );
};

export const hasCustomLocationItemsEnabled = (): boolean => {
  return useFeatureFlipper(FeatureFlag.CustomLocationItems) ?? true;
};

export const useCanEditItemTags = (): boolean => {
  return useFeatureFlipper(FeatureFlag.EditableItemTags) ?? true;
};

export const useDisplayProcurementSection = (): boolean => {
  return useFeatureFlipper(FeatureFlag.DisplayProcurementSection) ?? false;
};

export const useSelfServiceFulfillmentWarehouse = (): boolean => {
  return (
    useFeatureFlipper(
      FeatureFlag.itemManagementServiceSelfServiceFulfillmentWarehouse,
    ) ?? false
  );
};

export const useMarketPriceFlag = (): boolean => {
  return useFeatureFlipper(FeatureFlag.invItemMarketPriceFlag) ?? false;
};

export const useMarketPriceVendorValidationFlag = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.invItemMarketPriceVendorValidation) ?? false
  );
};

export const useBulkDiscontinue = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.ItemManagementWebBulkDiscontinue) ?? false
  );
};

export const useCategoryImageFallback = (): boolean => {
  return useFeatureFlipper(FeatureFlag.CategoryImageFallback) ?? false;
};

export const useSelfServiceMarketingCollections = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.selfServiceMarketingCollections) ?? false
  );
};

export const useCategoryBanners = (): boolean => {
  return useFeatureFlipper(FeatureFlag.CategoryBanners) ?? false;
};

export const useCategoryBannerCustomDestinations = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.CategoryBannerCustomDestinations) ?? false
  );
};

export const useSelfServiceMasterHomepages = (): boolean => {
  return useFeatureFlipper(FeatureFlag.selfServiceMasterHomepages) ?? false;
};

export const usePoundsUomProcurement = (): boolean => {
  return useFeatureFlipper(FeatureFlag.PoundsUomProcurement) ?? false;
};

export const useItemIssues = (): boolean => {
  return useFeatureFlipper(FeatureFlag.ItemIssues) ?? false;
};

export const useCanDeleteCollection = (): boolean => {
  return useFeatureFlipper(FeatureFlag.DeleteCollection) ?? false;
};

export const useCatalogOosBuffer = (): boolean => {
  return useFeatureFlipper(FeatureFlag.CatalogOosBuffer) ?? false;
};

export const useCanInactivateItem = (): boolean => {
  return useFeatureFlipper(FeatureFlag.ItemInactivation) ?? false;
};

export const useNewItemPinning = (): boolean => {
  return useFeatureFlipper(FeatureFlag.NewItemPinning) ?? false;
};

export const useCrossMarketNewItemPinning = (): boolean => {
  return useFeatureFlipper(FeatureFlag.CrossMarketNewItemPinning) ?? false;
};

export const useSubstitutionOverrides = (): boolean => {
  return useFeatureFlipper(FeatureFlag.SubstitutionOverrides) ?? false;
};

export const useMarketOrderability = (): boolean => {
  return useFeatureFlipper(FeatureFlag.MarketOrderability) ?? false;
};

export const useMarketOrderabilityCustomMessage = (): boolean => {
  return (
    useFeatureFlipper(FeatureFlag.MarketOrderabilityCustomMessage) ?? false
  );
};

export const useMarketItemFlags = (): boolean => {
  return useFeatureFlipper(FeatureFlag.MarketItemFlags) ?? false;
};

export const useLifetimeLimitPerLocation = (): boolean => {
  return useFeatureFlipper(FeatureFlag.LifetimeLimitPerLocation) ?? false;
};
